import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Empty} from 'antd'

import {withTitle} from '@/router/utils'
import {Card, Container, LinkButton, PageContainer} from '@/components'

import {BankAccountCard} from './components'
import {BankAccountDeleteConfirmationModal} from './components/BankAccountCard/BankAccountDeleteConfirmationModal'
import {IBankAccountsStore} from './IBankAccountsStore'
import styles from './BankAccounts.scss'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {GoogleManagerTagClassNames} from '@/constants'

export const BankAccountsStoreSymbol = Symbol('BankAccountsStoreSymbol')

const BankAccounts: React.FC<null> = observer(() => {
    const {t} = useTranslation()
    const {
        bankAccounts,
        isLoading,
        onBankCardClick,
        onAddNewBankAccountClick,
        loadDossierBankAccounts,
        onDeleteBankAccount,
        initStore,
        onBack
    } = useInjection<IBankAccountsStore>(BankAccountsStoreSymbol)

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

    const handleDeleteSuccess = () => setIsSuccessModalOpen(true)
    const handleSuccessClose = () => setIsSuccessModalOpen(false)

    useEffect(() => {
        loadDossierBankAccounts()

        return () => {
            initStore()
        }
    }, [])

    return (
        <Container className={styles.container}>
            <BankAccountDeleteConfirmationModal
                open={isSuccessModalOpen}
                onClose={handleSuccessClose}
            />
            <PageContainer
                title={t('BankAccounts.bankAccounts')}
                isLoading={false}
                extra={
                    hasPermissions([PermissionsMap.merchant.bank_accounts.full]) && (
                        <LinkButton
                            title={t('BankAccounts.bankAccountCreate')}
                            onClick={onAddNewBankAccountClick}
                            tagClassName={
                                GoogleManagerTagClassNames.settings.bankAccounts.addNewBankAccount
                                    .addButton
                            }
                        />
                    )
                }
                onBack={onBack}
            >
                {isLoading && <Card loading={isLoading} />}
                {bankAccounts.map((bankAccount, index) => (
                    <>
                        <BankAccountCard
                            key={bankAccount.accountNumber}
                            accountName={bankAccount.bankDetail?.bankAccountName}
                            accountNumber={bankAccount.accountNumber}
                            bank={bankAccount.bankDetail?.bankName}
                            sortCode={bankAccount.bankDetail?.sortCode}
                            logo={bankAccount.bankDetail?.logo}
                            bankDetailId={bankAccount.bankDetail?.id}
                            associatedSettlementsOutlets={bankAccount.stores.map(
                                (store) => store.storeName
                            )}
                            accountTypes={bankAccount.accountTypes}
                            onClick={() => onBankCardClick(index)}
                            onDelete={onDeleteBankAccount}
                            onDeleteSuccess={handleDeleteSuccess}
                        />
                        <br />
                    </>
                ))}
                {bankAccounts.length === 0 && !isLoading && <Empty description={false} />}
            </PageContainer>
        </Container>
    )
})

export default withTitle(BankAccounts, 'BankAccounts.bankAccounts')
